import React, { useEffect, useState } from 'react';
import {
    TextField,
    Grid,
    FormControlLabel,
    Switch,
    CircularProgress,
    Snackbar,
    Alert,
} from '@mui/material';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { getCmsPageBySlug, updateCmsPage } from '../../../shared/services/cmsService';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

export const EditCmsPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();

    const [pageId, setPageId] = useState<string>('');
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        content: '',
        metaTitle: '',
        metaDescription: '',
        isActive: true
    });

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [alert, setAlert] = useState({ show: false, type: 'success' as 'success' | 'error', message: '' });
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        if (slug) {
            const fetchPage = async () => {
                try {
                    setFetching(true);
                    const response = await getCmsPageBySlug(slug);
                    if (response.success && response.data?.page) {
                        const data = response.data.page;
                        setPageId(data.id || '');
                        setFormData({
                            title: data.title || '',
                            slug: data.slug || '',
                            content: data.content || '',
                            metaTitle: data.metaTitle || '',
                            metaDescription: data.metaDescription || '',
                            isActive: data.isActive ?? true
                        });
                    }
                } catch (error: any) {
                    console.error('Error fetching CMS page:', error);
                    setAlert({ show: true, type: 'error', message: 'Error loading page details' });
                } finally {
                    setFetching(false);
                }
            };
            fetchPage();
        }
    }, [slug]);

    const slugify = (text: string) => {
        return text
            .toString()
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')     // Replace spaces with -
            .replace(/[^\w-]+/g, '')  // Remove all non-word chars
            .replace(/--+/g, '-')     // Replace multiple - with single -
            .replace(/^-+/, '')       // Trim - from start of text
            .replace(/-+$/, '');      // Trim - from end of text
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;

        setFormData(prev => {
            const newData = {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };

            // Auto-generate slug if title changes
            if (name === 'title') {
                newData.slug = slugify(value);
            }

            return newData;
        });

        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleContentChange = (value: string) => {
        setFormData(prev => ({
            ...prev,
            content: value
        }));

        if (errors.content) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.content;
                return newErrors;
            });
        }
    };

    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
            ['link', 'image', 'video'],
            ['clean'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
        ],
    };

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list', 'bullet', 'indent',
        'link', 'image', 'video',
        'color', 'background',
        'align',
    ];

    const validate = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.title || formData.title.trim() === '') {
            newErrors.title = 'Title is required.';
        }
        if (!formData.slug || formData.slug.trim() === '') {
            newErrors.slug = 'Slug is required.';
        }
        if (!formData.content || formData.content.trim() === '') {
            newErrors.content = 'Content is required.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) return;

        try {
            setLoading(true);
            if (!slug) {
                throw new Error('Slug is missing');
            }
            await updateCmsPage(slug, formData);
            setAlert({ show: true, type: 'success', message: 'Page updated successfully' });
            setTimeout(() => {
                navigate('/cms');
            }, 1000);
        } catch (error: any) {
            console.error('Error updating CMS page:', error);
            setAlert({
                show: true,
                type: 'error',
                message: error.response?.data?.message || 'Failed to update page'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="add-edit-admin-container p-[40px]">
            <div className="add-edit-admin-content">
                <div className="form-wrapper max-w-6xl">
                    <div className="-ml-4">
                        <Link to="/cms" className="back-link">
                            <ArrowBack className="text-[#0EA5E9]" />
                            <span className="ml-2 text-[#0EA5E9]">Back</span>
                        </Link>
                    </div>
                    <div className="title text-2xl sm:text-5xl">
                        Edit CMS Page
                    </div>

                    <div className="form-card">
                        {fetching ? (
                            <div className="flex flex-col items-center justify-center p-12">
                                <CircularProgress size={50} />
                                <p className="mt-4 text-gray-500">Loading page details...</p>
                            </div>
                        ) : (
                            <>
                                {alert.show && (
                                    <Alert
                                        severity={alert.type}
                                        sx={{ mb: 4, borderRadius: '12px' }}
                                        onClose={() => setAlert({ ...alert, show: false })}
                                    >
                                        {alert.message}
                                    </Alert>
                                )}

                                <form onSubmit={handleSubmit} className="form space-y-4">
                                    <div className="mb-6">
                                        <div className="text-2xl font-bold tracking-tight text-gray-900">
                                            Page Information
                                        </div>
                                    </div>

                                    <Grid container spacing={3}>
                                        {/* Title */}
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Title"
                                                name="title"
                                                value={formData.title}
                                                onChange={handleChange}
                                                fullWidth
                                                required
                                                placeholder="E.g. About Us"
                                                error={!!errors.title}
                                                helperText={errors.title}
                                            />
                                        </Grid>

                                        {/* Slug */}
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Slug"
                                                name="slug"
                                                value={formData.slug}
                                                fullWidth
                                                InputProps={{
                                                    readOnly: true,
                                                }}
                                                sx={{
                                                    '& .MuiInputBase-input.Mui-readOnly': {
                                                        backgroundColor: '#f5f5f5',
                                                        cursor: 'not-allowed'
                                                    }
                                                }}
                                                helperText="Slug is automatically generated from Title"
                                            />
                                        </Grid>

                                        {/* Meta Title */}
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Meta Title"
                                                name="metaTitle"
                                                value={formData.metaTitle}
                                                onChange={handleChange}
                                                fullWidth
                                                placeholder="SEO title"
                                            />
                                        </Grid>

                                        {/* Meta Description */}
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Meta Description"
                                                name="metaDescription"
                                                value={formData.metaDescription}
                                                onChange={handleChange}
                                                fullWidth
                                                placeholder="SEO description"
                                            />
                                        </Grid>

                                        {/* Content */}
                                        <Grid item xs={12}>
                                            <div className="mb-2 block text-sm font-medium text-gray-700">
                                                Content <span className="text-red-500">*</span>
                                            </div>
                                            <div className={`editor-wrapper ${errors.content ? 'border-red-500' : ''}`}>
                                                <ReactQuill
                                                    theme="snow"
                                                    value={formData.content}
                                                    onChange={handleContentChange}
                                                    modules={modules}
                                                    formats={formats}
                                                    placeholder="Enter page content..."
                                                    style={{ height: '300px', marginBottom: '50px' }}
                                                />
                                            </div>
                                            {errors.content && (
                                                <p className="mt-1 text-xs text-red-600">{errors.content}</p>
                                            )}
                                        </Grid>

                                        {/* Active Switch */}
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        name="isActive"
                                                        checked={formData.isActive}
                                                        onChange={handleChange}
                                                        color="primary"
                                                    />
                                                }
                                                label="Is Active"
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* Action Buttons */}
                                    <div className="form-actions mt-8">
                                        <AppButton
                                            type="button"
                                            variant="cancel"
                                            onClick={() => navigate('/cms')}
                                            sx={{
                                                minWidth: '120px',
                                                backgroundColor: '#8E8E93',
                                                borderColor: '#8E8E93',
                                                color: '#fff',
                                                '&:hover': { backgroundColor: '#7a7a7f', borderColor: '#7a7a7f' }
                                            }}
                                        >
                                            Cancel
                                        </AppButton>

                                        <AppButton
                                            type="submit"
                                            variant="primary"
                                            disabled={loading}
                                            loading={loading}
                                            sx={{ minWidth: '120px' }}
                                        >
                                            Update Page
                                        </AppButton>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Snackbar */}
            <Snackbar
                open={alert.show}
                autoHideDuration={4000}
                onClose={() => setAlert({ ...alert, show: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={alert.type} variant="filled">
                    {alert.message}
                </Alert>
            </Snackbar>
        </div>
    );
};
